// src/modules/games/components/Show/ChallengeCreation.js
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import useClassrooms from "../../hooks/useClassrooms";
import { Buttonz, BodyCode, GameTitle, MessageContainer } from './Duels';

// 1. IMPORT THE NEW TOPIC SELECTION COMPONENT
import DuelTopicSelection from "./DuelTopicSelection";

// --- Styled Components for Challenge Creation ---

const ChallengeContainer = styled.div`
    display: flex;
    gap: 20px;
    width: 90%;
    max-width: 1200px;
    height: 600px; 
    padding: 20px;
`;

const ListContainer = styled.div`
  background: #ffffff; 
  border: 2px solid #673b8b;
  border-radius: 1em;
  padding: 10px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  
  h2 {
    color: #673c8c;
    font-size: 2em;
    margin: 0.5em 0;
    text-align: center;
    border-bottom: 2px solid #af9dc4;
  }
`;

const PupilListContainer = styled(ListContainer)`
    flex: 2; /* Left side (Pupils) is wider */
    max-height: 100%;
`;

const ClassroomListWrapper = styled.div`
    flex: 1; /* Right side container */
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-height: 100%;
`;

const ClassroomListContainer = styled(ListContainer)`
    flex-grow: 1; /* Makes the list take up the remaining space */
`;

// Yellow list button styling
const ChallengeListButton = styled.button`
    display: block;
    background: rgb(234, 146, 19);
    background: linear-gradient(
        0deg,
        rgba(234, 146, 19, 1) 0%,
        rgba(255, 186, 32, 1) 100%
    );
    border: 2px solid #e88f12;
    color: #fff;
    font-size: 1.2em;
    padding: 10px;
    margin-bottom: 8px;
    border-radius: 0.5em;
    cursor: pointer;
    transition: background 0.1s, opacity 0.2s;
    text-align: center;
    
    &:hover:not(:disabled) {
        filter: brightness(1.1);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;


// Button used for both Pupil and Class selection
const SelectButton = styled(ChallengeListButton)`
    text-align: left;
    background: #eeeaf5;
    color: #673b8c;
    border-color: #af9dc4;
    
    &:hover:not(:disabled) {
        background: #af9dc4;
        color: #fff;
        filter: none;
    }
`;

// Selected state for Pupil and Class selection
const SelectedButton = styled(ChallengeListButton)`
    text-align: left; /* Keep text left-aligned for pupil list */
    background: #673b8b;
    color: #fff;
    border-color: #543171;
    &:hover:not(:disabled) {
        background: #543171;
        color: #fff;
        filter: none;
    }
`;

const CreateChallengeTopButton = styled(ChallengeListButton)`
    height: 60px;
    line-height: 40px;
    font-size: 2em;
    margin: 0;
    text-align: center;
`;


const ChallengeCreation = ({ game, currentUserId, onBack }) => {

    const { classrooms, isLoading, error } = useClassrooms();
    const [selectedClassroom, setSelectedClassroom] = useState(null);
    const [selectedPupil, setSelectedPupil] = useState(null);

    // 2. NEW STATES TO MANAGE STEP AND DATA
    // 'pupil_selection' is the initial view.
    const [duelCreationStep, setDuelCreationStep] = useState('pupil_selection');
    const [selectedPupilForDuel, setSelectedPupilForDuel] = useState(null);

    // Set the first classroom as selected by default once data loads
    useEffect(() => {
        if (classrooms && classrooms.classrooms && classrooms.classrooms.length > 0) {
            if (!selectedClassroom) {
                setSelectedClassroom(classrooms.classrooms[0]);
            }
        }
    }, [classrooms, selectedClassroom]);


    if (isLoading) {
        return (
            <ChallengeContainer>
                <MessageContainer style={{ flex: 3 }}>Loading Classrooms...</MessageContainer>
            </ChallengeContainer>
        );
    }

    if (error || !classrooms || !classrooms.classrooms) {
        return (
            <ChallengeContainer>
                <MessageContainer style={{ flex: 3, color: 'red' }}>
                    Error loading classrooms: {error || 'No data received.'}
                </MessageContainer>
            </ChallengeContainer>
        );
    }

    const currentUserIdInt = parseInt(currentUserId, 10);

    // Pupils for the currently selected classroom, EXCLUDING the current user's ID
    const currentPupils = selectedClassroom
        ? selectedClassroom.pupils.filter(p =>
            p.id !== currentUserIdInt &&
            !p.username.includes('@') // New check for @ symbol
        )
        : [];

    const handlePupilSelect = (pupil) => {
        if (selectedPupil && selectedPupil.id === pupil.id) {
            setSelectedPupil(null);
        } else {
            setSelectedPupil(pupil);
        }
    }

    // 3. UPDATED: Save pupil and switch to topic selection view
    const handleCreateChallenge = () => {
        if (selectedPupil) {
            console.log("Moving to topic selection for pupil:", selectedPupil.username);
            setSelectedPupilForDuel(selectedPupil);
            setDuelCreationStep('topic_selection');
        } else {
            console.log("Please select a pupil first.");
        }
    }

    // 4. NEW: Function to navigate back from topic selection
    const handleBackToPupilSelection = () => {
        setDuelCreationStep('pupil_selection');
        setSelectedPupilForDuel(null);
    }

    const exitGameFunction = () => {
        setDuelCreationStep('pupil_selection');
        setSelectedPupilForDuel(null);
        onBack();
    }




    // 5. CONDITIONAL RENDER: Show DuelTopicSelection if step is 'topic_selection'
    if (duelCreationStep === 'topic_selection') {
        return (
            <DuelTopicSelection
                game={game}
                selectedPupil={selectedPupilForDuel}
                onBack={handleBackToPupilSelection}// Back to pupil selection
                exitGame={exitGameFunction}
                pupil_id={currentUserId}
            />
        );
    }

    // Default Render Logic (Pupil Selection)
    return (
        <ChallengeContainer>
            {/* Pupil List (Left Side - 2x width) */}
            <PupilListContainer>
                <h2>
                    {selectedClassroom ? selectedClassroom.name : 'Select a Classroom'} Pupils
                </h2>

                {currentPupils.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#af9dc4', marginTop: '20px' }}>
                        No other pupils in this classroom.
                    </p>
                ) : (
                    currentPupils.map(pupil => {
                        const isSelected = selectedPupil && selectedPupil.id === pupil.id;
                        const PupilButton = isSelected ? SelectedButton : SelectButton;

                        return (
                            <PupilButton
                                key={pupil.id}
                                onClick={() => handlePupilSelect(pupil)}
                            >
                                {pupil.username}
                            </PupilButton>
                        );
                    })
                )}
            </PupilListContainer>

            {/* Classroom List (Right Side - 1x width) */}
            <ClassroomListWrapper>
                {/* Create Challenge Button */}
                <CreateChallengeTopButton
                    onClick={handleCreateChallenge}
                    disabled={!selectedPupil}
                >
                    Create Challenge
                </CreateChallengeTopButton>

                <ClassroomListContainer>
                    <h2>Classrooms</h2>
                    {classrooms.classrooms
                        .filter(classroom => classroom.name !== "Demo Class") // Filter out Demo Class
                        .map(classroom => {
                            const ClassroomButton = selectedClassroom && selectedClassroom.id === classroom.id
                                ? SelectedButton : SelectButton;

                            return (
                                <ClassroomButton
                                    key={classroom.id}
                                    onClick={() => {
                                        setSelectedClassroom(classroom);
                                        setSelectedPupil(null);
                                    }}
                                >
                                    {classroom.name}
                                </ClassroomButton>
                            );
                        })}
                </ClassroomListContainer>
            </ClassroomListWrapper>

        </ChallengeContainer>
    );
};

export default ChallengeCreation;